import { Box, Typography } from '@mui/material';

import { IYearlyTrends } from 'src/types';
import { useLocales } from 'src/locales';
import { visuallyHidden } from 'src/components/table/table-head-custom';
import { ChartHoverColumns } from 'src/components/chart-hover';
import { useElementWidth } from 'src/sections/admin/sales-stats/cards/use-element-width';

type Props = {
  months: IYearlyTrends[];
};

/**
 * NAT-2519 — DSO and DPO, month by month, as two lines.
 *
 * Drawn as plain SVG rather than through a library: the FE has had no charting library since
 * NAT-1788 removed ApexCharts, and two lines over twelve points do not justify bringing one
 * back. Every colour reaches the SVG through `currentColor`, set by `sx` from palette tokens,
 * so the chart follows the colour scheme like the rest of the page.
 *
 * NAT-2481 — every month shows its DSO and DPO on hover and on keyboard focus.
 */

// The drawing's own coordinate space before the container is measured (and where it cannot be, as
// in jsdom). Once measured, the width follows the container and the height stays in real pixels,
// so a full-width page does not stretch the chart (NAT-2711).
const WIDTH = 600;
const HEIGHT = 180;
// Room above the tallest point, so its marker is not clipped by the top edge.
const TOP_PAD = 8;

const SERIES = [
  { key: 'dso', labelKey: 'trend_serie_dso', color: 'info.main' },
  { key: 'dpo', labelKey: 'trend_serie_dpo', color: 'warning.main' },
] as const;

export default function CashCycleTrendChart({ months }: Props) {
  const { t, currentLang } = useLocales('cashCycleDashboard');
  const [chartRef, measured] = useElementWidth<HTMLDivElement>();
  const drawWidth = measured ?? WIDTH;
  /** The point's centre sits in the middle of its month's column, so the labels below align. */
  const xOf = (index: number, count: number) => ((index + 0.5) / count) * drawWidth;
  const locale = currentLang.value === 'en' ? 'en-US' : 'it-IT';

  const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'short' });
  const fullMonthFormatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  const monthLabel = (row: IYearlyTrends) =>
    monthFormatter.format(new Date(row.year, row.month - 1, 1));
  const fullMonthLabel = (row: IYearlyTrends) =>
    fullMonthFormatter.format(new Date(row.year, row.month - 1, 1));

  // A floor of 1 keeps an all-zero year drawable: it lies flat on the baseline, not divided by 0.
  // `null` months (no flow to divide by, or a rolling window not yet full) don't count toward it.
  const max = Math.max(
    1,
    ...months
      .flatMap((row) => [row.dso, row.dpo])
      .filter((value): value is number => value !== null),
  );
  const yOf = (value: number) => HEIGHT - (value / max) * (HEIGHT - TOP_PAD);

  /**
   * A series' contiguous runs of known months, as `{ index, value }` pairs. A `null` month ends
   * the current run rather than being interpolated through, so the line shows a gap where a
   * figure is unknown instead of a dip toward zero.
   */
  const runsOf = (key: 'dso' | 'dpo'): { index: number; value: number }[][] => {
    const runs: { index: number; value: number }[][] = [];
    let current: { index: number; value: number }[] = [];
    months.forEach((row, index) => {
      const value = row[key];
      if (value === null) {
        if (current.length) runs.push(current);
        current = [];
      } else {
        current.push({ index, value });
      }
    });
    if (current.length) runs.push(current);
    return runs;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} aria-hidden>
        {SERIES.map((series) => (
          <Box key={series.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 12, height: 3, borderRadius: 1, bgcolor: series.color }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t(series.labelKey)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        {/* The scale, in HTML rather than SVG text so it uses a Typography variant. */}
        <Box
          aria-hidden
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'right',
            pb: 3,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {max}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            0
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box ref={chartRef} sx={{ position: 'relative' }}>
            <Box
              component="svg"
              viewBox={`0 0 ${drawWidth} ${HEIGHT}`}
              aria-hidden
              sx={{
                display: 'block',
                width: '100%',
                height: measured ? `${HEIGHT}px` : 'auto',
                overflow: 'visible',
              }}
            >
              {[0, 0.5, 1].map((fraction) => (
                <Box
                  key={fraction}
                  component="line"
                  x1={0}
                  x2={drawWidth}
                  y1={HEIGHT - fraction * (HEIGHT - TOP_PAD)}
                  y2={HEIGHT - fraction * (HEIGHT - TOP_PAD)}
                  stroke="currentColor"
                  strokeWidth={1}
                  sx={{ color: 'divider' }}
                />
              ))}
              {SERIES.map((series) => (
                <Box key={series.key} component="g" sx={{ color: series.color }}>
                  {runsOf(series.key).map((run, runIndex) => (
                    <polyline
                      key={runIndex}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      strokeLinejoin="round"
                      points={run
                        .map(({ index, value }) => `${xOf(index, months.length)},${yOf(value)}`)
                        .join(' ')}
                    />
                  ))}
                  {months.map((row, index) => {
                    const value = row[series.key];
                    if (value === null) return null;
                    return (
                      <circle
                        key={`${row.year}-${row.month}`}
                        cx={xOf(index, months.length)}
                        cy={yOf(value)}
                        r={4}
                        fill="currentColor"
                      />
                    );
                  })}
                </Box>
              ))}
            </Box>
            <ChartHoverColumns
              label={t('trend_grafico')}
              data={months.map((row) => ({
                key: `${row.year}-${row.month}`,
                title: fullMonthLabel(row),
                lines: SERIES.map((series) => ({
                  label: t(series.labelKey),
                  value: row[series.key] === null ? '—' : String(row[series.key]),
                  color: series.color,
                })),
              }))}
            />
          </Box>

          <Box
            aria-hidden
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${months.length}, 1fr)`,
              mt: 0.5,
            }}
          >
            {months.map((row) => (
              <Typography
                key={`${row.year}-${row.month}`}
                variant="caption"
                sx={{ color: 'text.secondary', textAlign: 'center' }}
              >
                {monthLabel(row)}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* The drawing is hidden from assistive technology; this table carries the same figures. */}
      <Box sx={visuallyHidden}>
        <table>
          <caption>{t('trend_titolo')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('mese')}</th>
              {SERIES.map((series) => (
                <th key={series.key} scope="col">
                  {t(series.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {months.map((row) => (
              <tr key={`${row.year}-${row.month}`}>
                <th scope="row">{fullMonthLabel(row)}</th>
                <td>{row.dso ?? '—'}</td>
                <td>{row.dpo ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
